import { useQuery } from "@tanstack/react-query";
import { bootstrapQueryOptions } from "~/app/api/pageData";
import type {
  BootstrapData,
  ConfigurationData,
  ExperimentPlatformData,
  FormattingData,
  ResourceData,
  UrlsData,
} from "~/app/store/bffApi";

const selectConfiguration = (data: BootstrapData) => data.configuration;
const selectFormatting = (data: BootstrapData) => data.formatting;
const selectResource = (data: BootstrapData) => data.resource;
const selectUrls = (data: BootstrapData) => data.urls;
const selectExperiment = (data: BootstrapData) => data.experiment;
const selectAffiliateExpirationTimeHours = (data: BootstrapData) =>
  data.configuration.affiliateExpirationTimeHours ?? 24;

export function useBootstrapSelector<T>(select: (data: BootstrapData) => T): T | null {
  return (
    useQuery({
      ...bootstrapQueryOptions(),
      select,
    }).data ?? null
  );
}

/**
 * Compatibility hook for existing consumers. Prefer focused selector hooks for new code.
 */
export function useAppConfig(): BootstrapData | null {
  return useQuery(bootstrapQueryOptions()).data ?? null;
}

export function useBootstrapConfiguration(): ConfigurationData | null {
  return useBootstrapSelector(selectConfiguration);
}

export function useAffiliateExpirationTimeHours(): number {
  return useBootstrapSelector(selectAffiliateExpirationTimeHours) ?? 24;
}

export function useBootstrapFormatting(): FormattingData | null {
  return useBootstrapSelector(selectFormatting);
}

export function useBootstrapResources(): ResourceData | null {
  return useBootstrapSelector(selectResource);
}

export function useBootstrapUrls(): UrlsData | null {
  return useBootstrapSelector(selectUrls);
}

export function useExperimentPlatform(): ExperimentPlatformData | null {
  return useBootstrapSelector(selectExperiment);
}
